import {
  Component, Input, OnChanges, SimpleChanges,
  AfterViewInit, ViewChild, ElementRef,
  ChangeDetectionStrategy, OnDestroy, NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapConfig } from '../Services/widget.models';

declare const google: any;

@Component({
  selector: 'app-map-widget',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="map-shell">
      <div class="map-header" *ngIf="config.title">
        <span class="map-title">{{ config.title }}</span>
        <span class="map-count">{{ config.data.length }} locations</span>
      </div>
      <div class="map-container" #mapContainer></div>
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; height: 100%; }
    .map-shell {
      display: flex; flex-direction: column;
      width: 100%; height: 100%; box-sizing: border-box;
      background: var(--dsh-primary, #fff);
      border-radius: 16px; box-shadow: 0 2px 12px rgba(0,0,0,.08);
      padding: 18px; overflow: hidden;
    }
    .map-header {
      display: flex; align-items: center;
      justify-content: space-between;
      margin-bottom: 10px; flex-shrink: 0;
    }
    .map-container { flex: 1; min-height: 0; width: 100%; border-radius: 10px; overflow: hidden; }
    .map-title { font-weight: 600; }
    .map-count { font-size: 12px; opacity: .7; }
  `]
})
export class MapWidgetComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() config!: MapConfig;
  @Input() langCode: 'en' | 'ar' = 'en';          // ← NEW
  @ViewChild('mapContainer') mapRef!: ElementRef<HTMLDivElement>;

  private map?: any;
  private markers: any[] = [];
  private initialized = false;

  // Keys that should never appear in popup cards
  private readonly HIDDEN_KEYS = new Set([
    'widgetid', 'widget_id', 'widgetId',
    'lat', 'lng', 'latitude', 'longitude'
  ]);

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.initialized = true;
    this.initMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.initialized) {
      this.clearMarkers();
      this.plotMarkers();
    }
  }

  ngOnDestroy(): void { this.clearMarkers(); }

  // ── Map init ────────────────────────────────────────────────
  private initMap(): void {
    if (typeof google === 'undefined') {
      console.warn('Google Maps JS API not loaded.');
      return;
    }
    const center = this.computeCenter();
    this.zone.runOutsideAngular(() => {
      this.map = new google.maps.Map(this.mapRef.nativeElement, {
        center,
        zoom: this.config.zoom ?? 13,
        mapTypeId: 'satellite',
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        rotateControl: false,
        tilt: 0
      });
      this.plotMarkers();
      this.fitToBounds();
    });
  }

  // ── Marker plotting ─────────────────────────────────────────
  private plotMarkers(): void {
    if (!this.map || !this.config.data.length) return;

    const latKey      = this.config.latKey;
    const lngKey      = this.config.lngKey;
    const pinLabelKey = this.config.pinLabelKey;  // e.g. 'areaCount'

    // 1. Group rows by "lat|lng" key so one pin = one popup with N cards
    const groups = new Map<string, { lat: number; lng: number; rows: any[] }>();

    for (const row of this.config.data) {
      const lat = parseFloat(this.resolveKey(row, latKey));
      const lng = parseFloat(this.resolveKey(row, lngKey));
      if (isNaN(lat) || isNaN(lng)) continue;

      const key = `${lat}|${lng}`;
      if (!groups.has(key)) groups.set(key, { lat, lng, rows: [] });
      groups.get(key)!.rows.push(row);
    }

    // 2. One shared InfoWindow
    const infoWindow = new google.maps.InfoWindow();

    // 3. One marker per unique coordinate
    groups.forEach(({ lat, lng, rows }) => {
      // Pin label: value from pinLabelKey (same for all rows at this pin)
      const pinLabel = pinLabelKey
        ? (this.resolveKey(rows[0], pinLabelKey) ?? '')
        : '';

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            this.buildPinSvg(String(pinLabel))
          ),
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 20)
        }
      });

      marker.addListener('click', () => {
        infoWindow.setContent(this.buildPopupContent(rows));
        infoWindow.open(this.map, marker);
      });

      this.markers.push(marker);
    });
  }

  // ── Pin SVG ─────────────────────────────────────────────────
  private buildPinSvg(label: string): string {
    return `
      <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="16" fill="#007cc4" stroke="white" stroke-width="3"/>
        <text x="50%" y="55%" text-anchor="middle" fill="white"
              font-size="13" font-weight="bold" font-family="Arial,sans-serif">
          ${label}
        </text>
      </svg>`;
  }

  // ── Popup: list of cards ─────────────────────────────────────
  private buildPopupContent(rows: any[]): string {
    const isAr  = this.langCode === 'ar';
    const dir   = isAr ? 'rtl' : 'ltr';

    const cards = rows.map((row, i) => {
      const fields = this.buildCardFields(row, isAr);
      return `
        <div style="
          background:#fff; border:1px solid #e5e7eb; border-radius:10px;
          padding:10px 12px; margin-bottom:${i < rows.length - 1 ? '10px' : '0'};
          font-family:'Segoe UI',Arial,sans-serif;
        ">
          ${fields}
        </div>`;
    }).join('');

    return `
      <div dir="${dir}" style="
        min-width:240px; max-width:320px;
        max-height:340px; overflow-y:auto;
        padding:4px 2px;
        font-family:'Segoe UI',Arial,sans-serif;
      ">
        ${cards || '<p style="color:#9ca3af;font-size:13px;">No data</p>'}
      </div>`;
  }

  // ── Card field rows ──────────────────────────────────────────
  private buildCardFields(row: Record<string, any>, isAr: boolean): string {
    const latKeyLc = this.config.latKey?.toLowerCase();
    const lngKeyLc = this.config.lngKey?.toLowerCase();
    const pinLabelKeyLc = this.config.pinLabelKey?.toLowerCase();

    // Collect visible keys; skip coordinates, widget id, and Arabic twin if En chosen (and vice-versa)
    const entries = Object.entries(row).filter(([key]) => {
      const kl = key.toLowerCase();
      if (this.HIDDEN_KEYS.has(kl))  return false;
      if (kl === latKeyLc || kl === lngKeyLc) return false;
      if (pinLabelKeyLc && kl === pinLabelKeyLc) return false;

      // Hide the "other language" duplicate:
      //   If showing EN → hide keys ending in 'Ar' when a sibling ending in 'En' exists
      //   If showing AR → hide keys ending in 'En' when a sibling ending in 'Ar' exists
      if (!isAr && key.endsWith('Ar')) {
        const enSibling = key.slice(0, -2) + 'En';
        if (enSibling in row) return false;
      }
      if (isAr && key.endsWith('En')) {
        const arSibling = key.slice(0, -2) + 'Ar';
        if (arSibling in row) return false;
      }

      return true;
    });

    // For AR mode, if a key ends with 'Ar' prefer it; map remaining 'En' keys to their Ar twin value
    const resolvedEntries = entries.map(([key, value]) => {
      let displayKey = key;
      let displayVal = value;

      if (isAr && key.endsWith('Ar')) {
        // Strip the 'Ar' suffix for a cleaner label
        displayKey = key.slice(0, -2);
      } else if (!isAr && key.endsWith('En')) {
        displayKey = key.slice(0, -2);
      }

      return { key: displayKey, value: displayVal };
    });

    return resolvedEntries.map(({ key, value }) => {
      const label = key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^\w/, c => c.toUpperCase())
        .trim();

      const displayValue = value === null || value === undefined || value === ''
        ? '—'
        : String(value);

      return `
        <div style="
          display:flex; justify-content:space-between; align-items:flex-start;
          gap:10px; padding:4px 0; border-bottom:1px solid #f3f4f6;
          font-size:12px; line-height:1.4;
        ">
          <span style="color:#6b7280; font-weight:500; white-space:nowrap; flex-shrink:0;">
            ${label}
          </span>
          <span style="color:#111827; font-weight:600; text-align:${isAr ? 'left' : 'right'}; word-break:break-word;">
            ${displayValue}
          </span>
        </div>`;
    }).join('');
  }

  // ── Fit bounds ───────────────────────────────────────────────
  private fitToBounds(): void {
    if (!this.config.bounds) return;
    const bounds = new google.maps.LatLngBounds(
      this.config.bounds.southwest,
      this.config.bounds.northeast
    );
    this.map.fitBounds(bounds);
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.map.setZoom(this.config.zoom ?? 15);
    });
  }

  // ── Helpers ──────────────────────────────────────────────────
  private resolveKey(row: any, key: string): any {
    if (key in row) return row[key];
    const found = Object.keys(row).find(k => k.toLowerCase() === key.toLowerCase());
    return found ? row[found] : null;
  }

  private clearMarkers(): void {
    this.markers.forEach(m => m.setMap(null));
    this.markers = [];
  }

  private computeCenter(): { lat: number; lng: number } {
    if (!this.config.data.length) return { lat: 0, lng: 0 };
    const lats = this.config.data.map(r => parseFloat(r[this.config.latKey])).filter(v => !isNaN(v));
    const lngs = this.config.data.map(r => parseFloat(r[this.config.lngKey])).filter(v => !isNaN(v));
    return {
      lat: lats.reduce((a, b) => a + b, 0) / lats.length,
      lng: lngs.reduce((a, b) => a + b, 0) / lngs.length
    };
  }
}